import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useNavigate } from "react-router";
import { authClient } from '../utils/amplifyClient';
import { getCurrentUser, fetchUserAttributes, AuthUser } from "aws-amplify/auth";
import { Authenticator, useAuthenticator } from "@aws-amplify/ui-react";
import { authenticatorServices } from "../utils/authenticatorServices";
import * as Sentry from "@sentry/browser";
import { SignInHeader } from "../components/Auth/SignInHeader/SignInHeader";
import { SignInFooter } from "../components/Auth/SignInFooter/SignInFooter";
import { SignUpHeaderUserRegistration } from "../components/Auth/SignUpHeader/SignUpHeader";
import { LoadingSpinner } from "../components/LoadingSpinner";
import { NotFound } from "./NotFound";
import { updateUser } from "../graphql/mutations";
import { getUser as getUserQuery } from "../graphql/queries";
import { useUserContext } from "../contexts/UserContext";
import TotalEquipIQLogoStacked from "../components/ui/icons-logos/TotalEquipIQLogoStacked";
import { Btn } from "../components/ui";
import { User } from "../types/schema";
import "../css/RegistrationPages.css"

export const Register = () => {
  const { userIdFromParams } = useParams();
  const [isLoading, setIsLoading] = useState(true);
  const [userNotFound, setUserNotFound] = useState(false);
  const [userFromId, setUserFromId] = useState<User | null>(null);
  const [isFinishingRegistration, setIsFinishingRegistration] = useState(false);
  const [linkError, setLinkError] = useState<string | null>(null);
  const { user, route } = useAuthenticator((context) => [
    context.user,
    context.route,
  ]);
  const { getUserDetails } = useUserContext();

  const navigate = useNavigate();
  const { toForgotPassword } = useAuthenticator();

  useEffect(() => {
    const getUserFromId = async () => {
      try {
        const result = await authClient.graphql({
          query: getUserQuery,
          variables: { id: userIdFromParams as string },
          authMode: "apiKey",
        });
        setIsLoading(false);
        if (!result.data.getUser) {
          // User record was deleted by admin
          return setUserNotFound(true);
        }
        if (result.data.getUser.cognitoId) {
          // Already registered — show sign-in instead of sign-up so they can recover
          setUserFromId(result.data.getUser as User);
          return;
        }
        setUserFromId(result.data.getUser as User);
      } catch (error) {
        console.log("error", error);
        Sentry.captureException(error);
        setIsLoading(false);
        setUserNotFound(true);
      }
    };
    if (!userFromId && userIdFromParams) {
      getUserFromId();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const updateUserWithCognitoId = async (currentUser: AuthUser) => {
      setIsFinishingRegistration(true);
      setLinkError(null);
      try {
        const attributes = await fetchUserAttributes();

        // Re-fetch the user record to get the latest state
        const result = await authClient.graphql({
          query: getUserQuery,
          variables: { id: userIdFromParams as string },
          authMode: "apiKey",
        });

        const freshUser = result.data.getUser;
        if (!freshUser) {
          // User record was deleted — account deactivated
          setIsFinishingRegistration(false);
          return setUserNotFound(true);
        }

        // Case 1: No cognitoId yet — link this Cognito account to the User record
        if (!freshUser.cognitoId) {
          const input = {
            id: userIdFromParams as string,
            cognitoId: currentUser.userId,
            name: attributes.name || freshUser.name,
          };
          await authClient.graphql({
            query: updateUser,
            variables: { input },
            authMode: "userPool",
          });
          await getUserDetails();
          navigate("/", { replace: true });
          return;
        }

        // Case 2: cognitoId matches current user — already linked, just redirect
        if (freshUser.cognitoId === currentUser.userId) {
          await getUserDetails();
          navigate("/", { replace: true });
          return;
        }

        // Case 3: cognitoId belongs to someone else — show not found
        setIsFinishingRegistration(false);
        setUserNotFound(true);
      } catch (error) {
        console.log("error fetching user attributes", error);
        Sentry.captureException(error);
        setIsFinishingRegistration(false);
        setLinkError(
          "Your account was confirmed, but we could not finish linking it to the company invite. Try signing in, or open this invite link again.",
        );
      }
    };
    const getNewUser = async () => {
      try {
        const newUser = await getCurrentUser();
        await updateUserWithCognitoId(newUser);
      } catch (error) {
        console.log("error getting current user after confirm", error);
        Sentry.captureException(error);
        setIsFinishingRegistration(false);
        setLinkError(
          "Your confirmation succeeded, but we could not start your session. Try signing in with the same email.",
        );
      }
    };
    if (route === "authenticated" && !linkError) {
      getNewUser();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [route, userFromId, user]);

  const formFields = {
    signUp: {
      name: {
        placeholder: "Enter Your Name Here",
        isRequired: true,
        label: "Name:",
      },
    },
  };

  const components = {
    SignIn: {
      Header() {
        return <SignInHeader />;
      },
      Footer() {
        return (
          <SignInFooter
            toForgotPassword={toForgotPassword}
            forgotPasswordText="Forgot Password"
          />
        );
      },
    },
    SignUp: {
      Header() {
        return <SignUpHeaderUserRegistration />;
      },
    },
  };

  return (
    <LoadingSpinner
      content={
        userNotFound ? (
          <NotFound />
        ) : linkError ? (
          <div className="auth-wrapper-register">
            <div className="centered-wrapper-register register-link-error">
              <TotalEquipIQLogoStacked iconClasses="fixed-logo-mark" ariaLabel="TotalEquipIQ" />
              <h1>Almost there</h1>
              <p>{linkError}</p>
              <div className="register-link-error-actions">
                <Btn
                  variant="primary"
                  buttonText="Sign in"
                  buttonOnClick={() => navigate("/login", { replace: true })}
                />
                <Btn
                  variant="secondary"
                  buttonText="Try again"
                  buttonOnClick={() => {
                    setLinkError(null);
                    window.location.reload();
                  }}
                />
              </div>
            </div>
          </div>
        ) : isFinishingRegistration ? (
          <div className="auth-wrapper-register">
            <div className="centered-wrapper-register">
              <p>Finishing registration…</p>
            </div>
          </div>
        ) : (
          <>
            <div className="fixed-logo">
              <TotalEquipIQLogoStacked iconClasses="fixed-logo-mark" ariaLabel="TotalEquipIQ" />
            </div>
            <div className="auth-wrapper-register">
              <div className=" centered-wrapper-register">
                <Authenticator
                  services={authenticatorServices}
                  initialState={userFromId?.cognitoId ? "signIn" : "signUp"}
                  formFields={formFields}
                  components={components}
                  loginMechanisms={["email"]}
                ></Authenticator>
              </div>
            </div>
          </>
        )
      }
      isLoading={isLoading}
    />
  );
};
